import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import type { Point } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router-dom';
import { ChartBaseProps } from '../types/chart-types';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

interface LivisPieDonutChartProps extends ChartBaseProps {
  height?: number;
  noDataMessage?: string;
  noDataMessageSubtitle?: string;
}

const LivisPieDonutChart: React.FC<LivisPieDonutChartProps> = ({
  data,
  title,
  subtitle,
  x_title,
  y_title,
  tooltip = true,
  legend = false,
  onClickPoint,
  x_range,
  y_range,
  height = 300,
  multiple = false,
  plotOptions,
  name,
  type = 'pie',
  handleNavigate = () => {null},
  noDataMessage = 'No Data to Display',
  noDataMessageSubtitle = ''
}) => {
  const { t: translate } = useTranslation();

  Highcharts.setOptions({
    lang: {
      thousandsSep: ','
    }
  });

  const navigate = useNavigate();

  // Check for empty data
  const hasData = Array.isArray(data) && data.length > 0 && data.some((series: any) => {
    if (Array.isArray(series?.data)) return series.data.length > 0;
    return true;
  });

  if (!hasData) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <p style={{ color: 'var(--livis-color-primary-gray)', textAlign: "center", margin: 0, fontWeight: 500 }}>
          {translate(noDataMessage)}
        </p>
        {noDataMessageSubtitle && (
          <p style={{ color: 'var(--livis-color-primary-gray)', fontSize: "0.8rem", textAlign: "center", margin: 0, opacity: 0.8 }}>
            {translate(noDataMessageSubtitle)}
          </p>
        )}
      </div>
    );
  }

  let options = {};

  switch (true) {
    case !multiple:
      options = {
        chart: {
          type: type,
          height: height,
          backgroundColor: 'transparent',
        },
        title: { text: title },
        subtitle: { text: subtitle },
        xAxis: {
          title: { text: x_title },
          categories: x_range,
        },
        yAxis: {
          title: { text: y_title },
          categories: y_range,
        },
        series: data,
        credits: { enabled: false },
        legend: { enabled: legend },
        tooltip: {
          enabled: tooltip,
          pointFormat:
            name === "inspection-station"
              ? undefined
              : "{series.name}: <b>{point.y:,.2f}</b>",
          useHTML: name === "inspection-station",
          formatter:
            name === "inspection-station"
              ? function (this: Highcharts.TooltipFormatterContextObject) {
                const point = this.point as Highcharts.Point;
                const name = (point && (point as any).name) || this.key;
                const y =
                  typeof this.y === "number"
                    ? Highcharts.numberFormat(this.y, 0)
                    : this.y;
                return `
                  <div style="text-align:left;">
                    <div><b>${t("Top 5 Defects")}</b></div>
                    <div>${name}: <b>${y}</b></div>
                    <div style="text-decoration: underline; margin-top: 4px;">${t("Click to go to reports")}</div>
                  </div>`;
              }
              : undefined,
        },
        plotOptions: {
          ...plotOptions,
          pie: {
            margin: [20, 20, 20, 20],
            ...(type === 'donut' && { innerSize: '40%' }),
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %',
              distance: 8,
              connectorPadding: 2,
              connectorWidth: 1,
              softConnector: true,
              style: {
                color: 'var(--livis-black-font-color)',
                textOutline: 'none',
              },
            },
            showInLegend: legend
          },
          series: {
            point: {
              events: {
                click: function (this: Point) {
                  if (onClickPoint) {
                    onClickPoint({
                      x: this.x,
                      y: this.y,
                      series: {
                        name: this.series.name,
                        type: this.series.type
                      },
                      point: this
                    });
                  }

                  const seriesType = this.series.type;
                  const userOptions = this.series.userOptions as {
                    data?: [string, number][],
                    valuesWithoutFormatting?: [string, number][]
                  };

                  if (seriesType === 'pie' && Array.isArray(userOptions.data) && Array.isArray(userOptions.valuesWithoutFormatting)) {
                    const label = this.name;
                    const value = this.y;
                    const match = userOptions.valuesWithoutFormatting.find((entry) => entry[1] === value);
                    const key = match ? match[0] : undefined;
                    console.log('Pie slice clicked. Label:', label, 'Key in valuesWithoutFormatting:', key, 'Full point:', this);
                    if (name === "inspection-station") {
                      console.log("navigating", key);
                      handleNavigate("defect", key);
                    }
                  } else {
                    console.log('Clicked value:', this.y, 'at x:', this.x, 'Full point:', this);
                  }
                }
              }
            }
          },
          area: {
            pointStart: 1999,
            marker: {
              enabled: false,
              symbol: "circle",
              radius: 2,
              states: {
                hover: { enabled: true },
              },
            },
          },
        },
      };
      break;

    case multiple:
      options = {
        chart: {
          type: type,
          height: height,
        },
        title: { text: title },
        subtitle: { text: subtitle },
        yAxis: {
          title: { text: y_title },
        },
        xAxis: {
          title: { text: x_title },
          allowDecimals: false,
          categories: x_range,
        },
        legend: {
          layout: "vertical",
          align: "right",
          verticalAlign: "middle",
          enabled: legend,
        },
        series: data,
        credits: { enabled: false },
        tooltip: {
          enabled: tooltip,
          pointFormat: "{series.name} Livis<b>{point.y:,.0f}</b><br/> {point.x}",
        },
        plotOptions: {
          pie: {
            margin: [20, 20, 20, 20],
            ...(type === 'donut' && { innerSize: '40%' }),
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %',
              distance: 8,
              connectorPadding: 2,
              connectorWidth: 1,
              softConnector: true,
              style: {
                color: 'var(--livis-black-font-color)',
                textOutline: 'none',
              },
            },
            showInLegend: legend
          },
          series: {
            point: {
              events: {
                click: function (this: Point) {
                  if (onClickPoint) {
                    onClickPoint({
                      x: this.x,
                      y: this.y,
                      series: {
                        name: this.series.name,
                        type: this.series.type
                      },
                      point: this
                    });
                  }

                  const seriesType = this.series.type;
                  const userOptions = this.series.userOptions as {
                    data?: [string, number][],
                    valuesWithoutFormatting?: [string, number][]
                  };

                  if (seriesType === 'pie' && Array.isArray(userOptions.data) && Array.isArray(userOptions.valuesWithoutFormatting)) {
                    const label = this.name;
                    const value = this.y;
                    const match = userOptions.valuesWithoutFormatting.find((entry) => entry[1] === value);
                    const key = match ? match[0] : undefined;
                    console.log('Pie slice clicked. Label:', label, 'Key in valuesWithoutFormatting:', key, 'Full point:', this);
                    if (name === "inspection-station") {
                      console.log("navigating", key);
                      navigate(`/app/reports/${key}`);
                    }
                  } else {
                    console.log('Clicked value:', this.y, 'at x:', this.x, 'Full point:', this);
                  }
                }
              }
            }
          },
          area: {
            pointStart: 1999,
            marker: {
              enabled: false,
              symbol: "circle",
              radius: 2,
              states: {
                hover: { enabled: true },
              },
            },
          },
        },
      };
      break;

    default:
      break;
  }

  return (
    <HighchartsReact
      containerProps={{ style: { maxHeight: "100%", width: "100%" } }}
      highcharts={Highcharts}
      options={options}
    />
  );
};

LivisPieDonutChart.propTypes = {
  data: PropTypes.array.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  x_title: PropTypes.string,
  y_title: PropTypes.string,
  tooltip: PropTypes.bool,
  legend: PropTypes.bool,
  onClickPoint: PropTypes.func,
  x_range: PropTypes.array,
  y_range: PropTypes.array,
  height: PropTypes.number,
  multiple: PropTypes.bool,
  plotOptions: PropTypes.object,
  name: PropTypes.string,
  noDataMessage: PropTypes.string,
  noDataMessageSubtitle: PropTypes.string,
  type: PropTypes.oneOf([
    "line", "multi-line", "bar", "grouped-bar", "horizontal-bar",
    "combo", "dual-axis", "area", "stacked-area", "pie", "donut",
    "radar", "scatter", "bubble", "composed"
  ]) as any,
};

LivisPieDonutChart.defaultProps = {
  title: '',
  subtitle: '',
  x_title: '',
  y_title: '',
  tooltip: true,
  legend: false,
  onClickPoint: undefined,
  x_range: [],
  y_range: [],
  height: 300,
  multiple: false,
  plotOptions: {},
  name: '',
  type: 'pie',
  noDataMessage: 'No Data to Display',
  noDataMessageSubtitle: ''
};

export default LivisPieDonutChart;